import { useState } from "react";
import LazyImage from "../../shared/lazyImage";
import { toast } from "react-hot-toast";
import api from "../../../shared/utils/request";
import { useCart } from "../../../shared/context/CartContext";

const variantStyles = `
  @media (max-width: 767px) {
    .variant-card-mobile {
      padding: 8px !important;
    }
    .variant-card-mobile .variant-image-responsive {
      width: 80px !important;
      height: 80px !important;
    }
    .variant-card-mobile .variant-image-responsive img {
      height: 100% !important;
      padding: 2px !important;
    }
    .variant-card-mobile h6 {
      margin-bottom: 2px !important;
      font-size: 12px !important;
    }
    .variant-card-mobile .price-section {
      margin-bottom: 2px !important;
    }
    .variant-card-mobile .stock-section {
      margin-bottom: 4px !important;
    }
    .quantity-mobile {
      padding: 2px 4px !important;
      gap: 0 !important;
    }
    .quantity-mobile .quantity__minus,
    .quantity-mobile .quantity__plus {
      padding: 2px !important;
      font-size: 11px !important;
    }
    .quantity-mobile .quantity__input {
      width: 24px !important;
      height: 22px !important;
      font-size: 12px !important;
      -moz-appearance: textfield;
      appearance: textfield;
    }
    .quantity-mobile .quantity__input::-webkit-outer-spin-button,
    .quantity-mobile .quantity__input::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }
  @media (min-width: 768px) {
    .variant-image-responsive {
      width: 180px !important;
      height: 180px !important;
    }
    .variant-image-responsive img {
      height: 100% !important;
      padding: 8px !important;
    }
  }
`;

const VariantsPanel = ({ productDetails, activeVariant, setActiveVariant }) => {
  const variants = productDetails?.product_variants || [];
  const [activeTab, setActiveTab] = useState("customize");

  if (variants.length === 0) return null;

  return (
    <>
      <style>{variantStyles}</style>

      {/* Mobile Tabs */}
      <div className="d-md-none">
        <div
          className="d-flex p-1 rounded-10 mb-12"
          style={{
            background: "#f1f5f9",
            border: "1px solid #e2e8f0",
          }}
        >
          <button
            className={`flex-1 py-6 px-10 text-xs fw-semibold border-0 rounded-8 ${
              activeTab === "customize"
                ? "text-white"
                : "bg-transparent text-gray-500"
            }`}
            style={{
              background:
                activeTab === "customize" ? "var(--main-600)" : "transparent",
            }}
            onClick={() => setActiveTab("customize")}
          >
            <i className="ph ph-sliders-horizontal me-4" />
            Customize
          </button>
          <button
            className={`flex-1 py-6 px-10 text-xs fw-semibold border-0 rounded-8 ${
              activeTab === "description"
                ? "text-white"
                : "bg-transparent text-gray-500"
            }`}
            style={{
              background:
                activeTab === "description" ? "var(--main-600)" : "transparent",
            }}
            onClick={() => setActiveTab("description")}
          >
            <i className="ph ph-note-text me-4" />
            Description
          </button>
        </div>

        {activeTab === "customize" && (
          <div>
            {variants.map((variant) => (
              <VariantCard
                key={variant.id}
                variant={variant}
                productDetails={productDetails}
                isActive={activeVariant?.id === variant.id}
                onSelect={() => setActiveVariant(variant)}
              />
            ))}
          </div>
        )}

        {activeTab === "description" && (
          <div className="p-12">
            <div
              className="text-gray-600 text-sm mb-0"
              style={{ lineHeight: "1.7" }}
              dangerouslySetInnerHTML={{
                __html:
                  productDetails?.description ||
                  "<p>No description available</p>",
              }}
            />
          </div>
        )}
      </div>

      {/* Desktop View - No Tabs */}
      <div className="d-none d-md-block">
        <div>
          {variants.map((variant) => (
            <VariantCard
              key={variant.id}
              variant={variant}
              productDetails={productDetails}
              isActive={activeVariant?.id === variant.id}
              onSelect={() => setActiveVariant(variant)}
            />
          ))}
        </div>
      </div>
    </>
  );
};

const VariantCard = ({ variant, productDetails, isActive, onSelect }) => {
  const { syncCartCount } = useCart();
  const [addToCartLoading, setAddToCartLoading] = useState(false);
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [quantity, setQuantity] = useState(variant?.cart_count || 0);
  const [cartId, setCartId] = useState(null);
  const [qtyLoading, setQtyLoading] = useState(null);

  const fetchCartId = async () => {
    try {
      const cartList = await api.post("customer/getCartList");
      const item = cartList?.cartItems?.find(
        (c) =>
          c?.product_id === productDetails?.id && c?.variant_id === variant?.id,
      );
      setCartId(item?.cart_id ?? null);
      return item?.cart_id ?? null;
    } catch (error) {
      console.error("Failed to fetch cart id:", error);
      return null;
    }
  };

  const handleAddToCart = async (e) => {
    e.stopPropagation();
    const isToken = localStorage.getItem("token");
    if (!isToken) {
      window.showLoginModal?.();
      return;
    }

    try {
      setAddToCartLoading(true);
      const data = await api.post("customer/addToCart", {
        product_id: productDetails?.id,
        product_variant_id: variant?.id,
        quantity: 1,
        price: variant?.price,
      });
      if (data?.status === true) {
        toast.success(data?.message);
        syncCartCount(data?.cart_total_items);
        setQuantity(1);
        await fetchCartId();
      } else {
        toast.error(data?.message);
      }
      setAddToCartLoading(false);
    } catch (error) {
      setAddToCartLoading(false);
      console.error("Failed to add product to cart:", error);
    }
  };

  const handleQuantityChange = async (newQuantity, action) => {
    const isToken = localStorage.getItem("token");
    if (!isToken) {
      window.showLoginModal?.();
      return;
    }

    const currentCartId = cartId ?? (await fetchCartId());
    if (!currentCartId) {
      toast.error("Unable to update cart. Please try again.");
      return;
    }

    try {
      setQtyLoading(action);
      const response = await api.post("customer/updateCart", {
        cart_id: currentCartId,
        quantity: newQuantity,
        product_id: productDetails?.id,
        variant_id: variant?.id,
      });
      if (response?.status) {
        syncCartCount(response?.cart_total_items);
        setQuantity(newQuantity);
        if (newQuantity === 0) {
          setCartId(null);
        }
      } else {
        toast.error(response?.message);
      }
      setQtyLoading(null);
    } catch (error) {
      setQtyLoading(null);
      console.error("Failed to update cart quantity:", error);
    }
  };

  const handleToggleWishlist = async (e) => {
    e.stopPropagation();
    const isToken = localStorage.getItem("token");
    if (!isToken) {
      window.showLoginModal?.();
      return;
    }

    try {
      setWishlistLoading(true);
      const data = await api.post("customer/addToWishList", {
        product_id: productDetails?.id,
        variant_id: variant?.id,
      });
      if (data?.status === true) {
        setIsWishlisted((prev) => !prev);
        toast.success(data?.message);
      } else {
        toast.error(data?.message);
      }
      setWishlistLoading(false);
    } catch (error) {
      setWishlistLoading(false);
      console.error("Failed to toggle wishlist:", error);
    }
  };

  const isDisabled = addToCartLoading || variant?.stock <= 0;

  return (
    <div
      onClick={onSelect}
      className={`p-12 rounded-16 mb-8 transition-all variant-card-mobile ${
        isActive ? "bg-main-50" : "hover-bg-main-50"
      }`}
      style={{
        cursor: "pointer",
        border: isActive ? "2px solid var(--main-600)" : "1px solid #e5e7eb",
        boxShadow: isActive ? "0 2px 12px rgba(0,0,0,0.08)" : "none",
      }}
    >
      <div className="d-flex flex-row align-items-center gap-8 gap-md-16">
        <div className="flex-grow-1 min-w-0 order-2">
          <div className="text-start">
            <h6
              className={`text-sm mb-4 fw-semibold ${isActive ? "text-main-600" : "text-gray-900"}`}
            >
              {variant?.title}
            </h6>
            <div className="d-flex align-items-center justify-content-start gap-6 mb-4 flex-wrap price-section">
              <span className="text-lg fw-bold text-gray-900">
                ₹{variant?.sale_price}
              </span>
              {variant?.price !== variant?.sale_price && (
                <>
                  <span className="text-sm text-gray-400 text-decoration-line-through">
                    ₹{variant?.price}
                  </span>
                  <span className="text-xs fw-bold text-success-600 bg-success-50 px-6 py-2 rounded-pill">
                    {Math.round(
                      ((variant?.price - variant?.sale_price) /
                        variant?.price) *
                        100,
                    )}
                    % OFF
                  </span>
                </>
              )}
            </div>
            <div
              className="d-flex align-items-center justify-content-between gap-6 flex-wrap stock-section"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="d-flex align-items-center gap-6 flex-wrap">
                <span className="text-xs text-gray-500">
                  {variant?.quantity}
                </span>
                <span className="text-gray-300">|</span>
                {variant?.stock > 0 ? (
                  <span className="text-xs fw-medium text-success-600">
                    <i className="ph-fill ph-check-circle me-4" />
                    In Stock ({variant?.stock})
                  </span>
                ) : (
                  <span className="text-xs fw-medium text-danger-600">
                    <i className="ph-fill ph-x-circle me-4" />
                    Out of Stock
                  </span>
                )}
              </div>
              <div className="d-flex align-items-center gap-4 d-md-none">
                {quantity > 0 ? (
                  <>
                    {qtyLoading ? (
                      <span
                        className="spinner-border spinner-border-sm"
                        aria-hidden="true"
                      />
                    ) : (
                      <div className="bg-gray-100 rounded-pill py-4 px-8 flex-align quantity-mobile">
                        <button
                          type="button"
                          disabled={!!qtyLoading}
                          className="quantity__minus p-2 text-gray-700 hover-text-main-600 flex-center"
                          onClick={() =>
                            handleQuantityChange(quantity - 1, "minus")
                          }
                        >
                          <i className="ph ph-minus" />
                        </button>
                        <input
                          type="number"
                          className="quantity__input border-0 text-center w-32"
                          value={quantity}
                          style={{ background: "#e6e6e6" }}
                          readOnly
                        />
                        <button
                          type="button"
                          disabled={variant?.stock <= quantity || !!qtyLoading}
                          className="quantity__plus p-2 text-gray-700 hover-text-main-600 flex-center"
                          onClick={() =>
                            handleQuantityChange(quantity + 1, "plus")
                          }
                        >
                          <i className="ph ph-plus" />
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <button
                    onClick={handleAddToCart}
                    disabled={isDisabled}
                    className={`btn btn-icon rounded-pill flex-center ${
                      isActive ? "btn-main" : "bg-main-50 text-main-600"
                    } ${isDisabled ? "opacity-50 pe-none" : ""}`}
                    style={{
                      whiteSpace: "nowrap",
                      width: "50px",
                      height: "32px",
                      fontSize: "12px",
                      cursor: isDisabled ? "not-allowed" : "pointer",
                    }}
                  >
                    {addToCartLoading ? (
                      <span
                        className="spinner-border spinner-border-sm"
                        aria-hidden="true"
                      />
                    ) : (
                      <>
                        <i className="ph ph-shopping-cart" /> Add
                      </>
                    )}
                  </button>
                )}
                <button
                  onClick={handleToggleWishlist}
                  disabled={wishlistLoading}
                  className={`w-32 h-32 flex-center rounded-circle border text-sm ${
                    isWishlisted || variant?.in_wishlist
                      ? "bg-main-600 text-white border-main-600"
                      : "bg-main-50 text-main-600 border-main-100"
                  } ${wishlistLoading ? "opacity-50 pe-none" : ""}`}
                  style={{
                    cursor: wishlistLoading ? "not-allowed" : "pointer",
                  }}
                >
                  {wishlistLoading ? (
                    <span
                      className="spinner-border spinner-border-sm"
                      aria-hidden="true"
                    />
                  ) : (
                    <i
                      className={
                        isWishlisted || variant?.in_wishlist
                          ? "ph-fill ph-heart"
                          : "ph ph-heart"
                      }
                    />
                  )}
                </button>
              </div>
            </div>
          </div>
          <div
            className="d-none d-md-flex align-items-center justify-content-start gap-8 flex-wrap"
            onClick={(e) => e.stopPropagation()}
          >
            {quantity > 0 ? (
              <div className="bg-gray-100 rounded-pill py-4 px-8 flex-align">
                <button
                  type="button"
                  disabled={!!qtyLoading}
                  className="quantity__minus p-2 text-gray-700 hover-text-main-600 flex-center"
                  onClick={() => handleQuantityChange(quantity - 1, "minus")}
                >
                  {qtyLoading === "minus" ? (
                    <span
                      className="spinner-border spinner-border-sm"
                      aria-hidden="true"
                    />
                  ) : (
                    <i className="ph ph-minus" />
                  )}
                </button>
                <input
                  type="number"
                  className="quantity__input border-0 text-center w-32"
                  value={quantity}
                  style={{ background: "#e6e6e6" }}
                  readOnly
                />
                <button
                  type="button"
                  disabled={variant?.stock <= quantity || !!qtyLoading}
                  className="quantity__plus p-2 text-gray-700 hover-text-main-600 flex-center"
                  onClick={() => handleQuantityChange(quantity + 1, "plus")}
                >
                  {qtyLoading === "plus" ? (
                    <span
                      className="spinner-border spinner-border-sm"
                      aria-hidden="true"
                    />
                  ) : (
                    <i className="ph ph-plus" />
                  )}
                </button>
              </div>
            ) : (
              <button
                onClick={handleAddToCart}
                disabled={isDisabled}
                className={`btn rounded-pill flex-align justify-content-center gap-6 py-10 px-20 text-sm fw-semibold ${
                  isActive
                    ? "btn-main"
                    : "bg-main-50 text-main-600 hover-bg-main-600 hover-text-white"
                } ${isDisabled ? "opacity-50 pe-none" : ""}`}
                style={
                  isDisabled ? { cursor: "not-allowed" } : { cursor: "pointer" }
                }
              >
                {addToCartLoading ? (
                  <span
                    className="spinner-border spinner-border-sm"
                    aria-hidden="true"
                  />
                ) : (
                  <i className="ph ph-shopping-cart" />
                )}
                Add to Cart
              </button>
            )}
            <button
              type="button"
              onClick={handleToggleWishlist}
              disabled={wishlistLoading}
              className={`w-44 h-44 flex-center rounded-circle border text-lg ${
                isWishlisted || variant?.in_wishlist
                  ? "bg-main-600 text-white border-main-600"
                  : "bg-main-50 text-main-600 border-main-100 hover-bg-main-600 hover-text-white hover-border-main-600"
              } ${wishlistLoading ? "opacity-50 pe-none" : ""}`}
              style={
                wishlistLoading
                  ? { cursor: "not-allowed" }
                  : { cursor: "pointer" }
              }
            >
              {wishlistLoading ? (
                <span
                  className="spinner-border spinner-border-sm"
                  aria-hidden="true"
                />
              ) : (
                <i
                  className={
                    isWishlisted || variant?.in_wishlist
                      ? "ph-fill ph-heart"
                      : "ph ph-heart"
                  }
                />
              )}
            </button>
          </div>
        </div>
        <div
          className="flex-shrink-0 rounded-16 overflow-hidden d-flex flex-column align-items-center justify-content-center bg-white variant-card-image order-1 variant-image-responsive"
          style={{
            width: "100px",
            height: "100px",
            border: "1px solid #f0f0f0",
          }}
        >
          <LazyImage
            src={variant?.product_image}
            alt={variant?.title}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              padding: "4px",
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default VariantsPanel;
